<template>
  <div class="as-mypages-layout">
    <v-app-bar
      app
      dark
      clipped-left
      class="as-header mx-auto"
      height="64px"
    >

      <v-toolbar-title>
        <router-link :to="getLocalisedUrl('/')">
          <app-logo :show-tagline="true" />
        </router-link>
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-app-bar-nav-icon 
        class="d-md-none" 
        @click="drawer = !drawer"
      >
      </v-app-bar-nav-icon>

      <!-- Desktop: UserAccountMenu with language selector (accounts + no logout in header) -->
      <div class="d-none d-md-block">
        <user-account-menu
          variant="light"
          show-language
        />
      </div>
    </v-app-bar>

    <!-- Modern Sidebar Navigation -->
    <v-navigation-drawer
      v-model="drawer"
      app
      clipped
      :mobile-breakpoint="960"
      class="modern-sidebar"
      width="280"
    >
      <!-- Profile Section: Organization account or Personal account -->
      <div class="organization-profile-section" :key="activeAccountIdentifier">
        <!-- Organization account: org logo, org name, user name; next line: 3 actions -->
        <template v-if="isAccountTypeOrganization">
          <div class="organization-profile-content">
            <div class="organization-logo">
              <v-avatar 
                size="60" 
                color="var(--color-pallet-1)"
              >
                <img 
                  v-if="activeOrganization && (activeOrganization.logo_url || activeOrganization.src_logo_large)" 
                  :src="activeOrganization.src_logo_large || activeOrganization.logo_url" 
                  :alt="(activeOrganization && activeOrganization.name) + ' Logo'" 
                  class="organization-logo-image"
                >
                <v-icon v-else size="30" color="white">mdi-dumbbell</v-icon>
              </v-avatar>
            </div>
            <div class="organization-info">
              <!-- Always openable, even with one studio: the menu is where Mans profils and
                   Iziet live, so gating it on having several studios hid them from most trainers. -->
              <portal-account-menu>
                <template #activator="{ on, attrs }">
                  <div class="name-row name-row-clickable" v-bind="attrs" v-on="on">
                    <div class="name-row-center">
                      <h3 class="organization-name">{{ activeOrganization?.name }}</h3>
                      <p class="organization-user-name">{{ username }}</p>
                    </div>
                    <v-icon class="name-row-chevron" color="white" size="20">mdi-chevron-down</v-icon>
                  </div>
                </template>
              </portal-account-menu>
            </div>
          </div>
          <!-- Next line: 3 action buttons -->
          <div class="organization-actions mb-2">
            <v-btn
              icon
              small
              color="white"
              :href="getOrganizationPageUrl()"
              class="action-btn"
              :title="$t('layout.mypages.organization.view-page')"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="white"
              @click="copyOrganizationPage"
              class="action-btn"
              :title="$t('layout.mypages.organization.copy-url')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
            <v-btn
              icon
              small
              color="white"
              @click="editOrganizationProfile"
              class="as-action-icon"
              :title="$t('layout.mypages.organization.edit-profile')"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </div>
        </template>
        <!-- Participant account: generic icon, user name, "Participant account"; next line: org icons -->
        <template v-else-if="isAccountTypeParticipant">
          <div class="organization-profile-content">
            <div class="organization-logo">
              <v-avatar size="60" color="#1976D2">
                <v-icon size="30" color="white">mdi-account</v-icon>
              </v-avatar>
            </div>
            <div class="organization-info">
              <portal-account-menu>
                <template #activator="{ on, attrs }">
                  <div class="name-row name-row-clickable" v-bind="attrs" v-on="on">
                    <div class="name-row-center">
                      <h3 class="organization-name">{{ username }}</h3>
                      <span class="trainer-name">{{ $t('layout.account-switcher.participant-account') }}</span>
                    </div>
                    <v-icon class="name-row-chevron" color="white" size="20">mdi-chevron-down</v-icon>
                  </div>
                </template>
              </portal-account-menu>
            </div>
          </div>
          <!-- Next line: list of participant organizations as icons (with org logo when available) -->
          <div v-if="participantOrganizations && participantOrganizations.length" class="participant-orgs-row">
            <a
              v-for="org in participantOrganizations"
              :key="org.identifier"
              :href="getOrganizationPublicUrl(org)"
              class="participant-org-link"
              :title="$t('layout.mypages.organization.view-page') + ': ' + org.name"
            >
              <v-avatar size="36" color="var(--color-pallet-1)" class="participant-org-avatar">
                <img
                  v-if="org.src_logo_large || org.logo_url"
                  :src="org.src_logo_large || org.logo_url"
                  :alt="org.name"
                  class="participant-org-logo"
                >
                <v-icon v-else size="20" color="white">mdi-dumbbell</v-icon>
              </v-avatar>
            </a>
          </div>
        </template>
      </div>

      <!-- Profile Section Divider -->
      <v-divider class="profile-divider"></v-divider>

      <!-- Participant Menu Structure -->
      <template v-if="isAccountTypeParticipant">
        <!-- Dashboard Menu Item -->
        <div class="navigation-container mt-2">
          <v-list class="sidebar-menu" dense>
            <v-list-item
              :to="getLocalisedMyPagesUrl('participant-dashboard')"
              class="sidebar-menu-item"
              active-class="sidebar-menu-item-active"
              link
            >
              <v-list-item-icon class="sidebar-icon">
                <v-icon color="white">mdi-view-dashboard</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="sidebar-text">{{ $t('layout.mypages.navigation.participant-dashboard') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="hasPurchases"
              :to="getLocalisedMyPagesUrl(ParticipantSegment.PURCHASES)"
              class="sidebar-menu-item"
              active-class="sidebar-menu-item-active"
              link
            >
              <v-list-item-icon class="sidebar-icon">
                <v-icon color="white">mdi-receipt-text-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="sidebar-text">{{ $t('layout.mypages.navigation.participant-purchases') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>

        <!-- Space -->
        <div style="flex: 1;"></div>
      </template>

      <!-- Trainer Menu Structure: daily work | catalogues | settings (with its sections as sub-items while inside settings) -->
      <template v-if="isAccountTypeOrganization">
        <div class="navigation-container mt-2">
          <v-list class="sidebar-menu" dense>
            <template v-for="(item, index) in navigationItems">
              <v-divider v-if="item.type === NAV_ITEM_TYPE_DIVIDER" :key="`divider-${index}`" class="sidebar-divider" />
              <template v-else>
                <v-list-item
                  :key="item.route || item.href"
                  :to="item.external ? undefined : item.route"
                  :href="item.external ? item.href : undefined"
                  :target="item.external ? '_blank' : undefined"
                  :rel="item.external ? 'noopener noreferrer' : undefined"
                  :exact="Boolean(item.children)"
                  class="sidebar-menu-item"
                  active-class="sidebar-menu-item-active"
                  link
                >
                  <v-list-item-icon class="sidebar-icon">
                    <v-icon color="white">{{ item.icon }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="sidebar-text">{{ item.title }}</v-list-item-title>
                  </v-list-item-content>
                  <!-- Says the click leaves ASport before it happens. -->
                  <v-icon v-if="item.external" color="white" small>mdi-open-in-new</v-icon>
                </v-list-item>
                <template v-if="item.children && isSettingsRouteActive">
                  <v-list-item
                    v-for="child in item.children"
                    :key="child.routeName"
                    :to="{ name: child.routeName }"
                    class="sidebar-submenu-item"
                    active-class="sidebar-submenu-item-active"
                    link
                  >
                    <v-list-item-icon class="sidebar-icon">
                      <v-icon color="white" small>{{ child.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="sidebar-text">{{ child.title }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </template>
            </template>
          </v-list>
        </div>

        <v-divider></v-divider>
      </template>

      <!-- Bottom Actions - Fixed at bottom (shared for both participant and trainer) -->
      <template v-slot:append>
        <v-list class="sidebar-menu" dense>
          
          <!-- Mans profils lives in the header account menu (PortalAccountMenu); it belongs under
               the person's own name, not next to the studio's settings. Iziet stays here as well as
               there, because that is where people look for it. -->
          <!-- Logout button -->
          <v-list-item
            @click="logout"
            class="sidebar-menu-item sidebar-logout"
          >
            <v-list-item-icon class="sidebar-icon">
              <v-icon color="var(--color-red-1)">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="sidebar-text">{{ $t('layout.menu.logout') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>

    <!-- Mobile mainMenu -->
    
    <v-main class="mypage-view">
      <!-- "You are in a different time zone" notice (F14) - full-bleed strip, outside the content
           container, so it spans the page and stays visible while scrolling. -->
      <TimezoneNoticeBanner />
      <div class="my-pages-layout">
        <!-- Main Content Area -->
        <div class="main-content">
          <!-- Content Container -->
          <div class="content-container">
            <!-- Email Verification Banner -->
            <EmailVerificationBanner />
            <!-- "No plan chosen" reminder for owners/admins (S6) -->
            <PlanSelectionBanner />

            <!-- Page Content -->
            <router-view :key="$route.fullPath" />

          </div>
        </div>
      </div>
    </v-main>

    <v-footer
      padless
      class="as-footer"
    >
      <div class="footer-content">
        <p class="footer-text">
          {{ $t('layout.mypages.footer.copyright', { year: new Date().getFullYear() }) }}
        </p>
        <p class="footer-text">
          <a href="mailto:info@asport.lv" class="footer-link">{{ $t('layout.mypages.footer.contact') }}</a> • 
          <a href="/terms" class="footer-link">{{ $t('layout.mypages.footer.terms') }}</a>
        </p>
      </div>
    </v-footer>
  </div>
</template>
<script>

import urlUtil from '@/js/utils/urlUtil'
import { isMembershipModuleEnabled } from '@/js/utils/organizationUtil'
import { PAYMENT_GATEWAY_DISPLAY_NAME } from '@/js/api/private/TOrganizationRepository'
import { MyPagesPath, ParticipantSegment } from '@/router/myPagesPaths'

import { SETTINGS_SECTIONS, getSettingsSectionTitleKey, isSettingsRoute } from '@/domains/settings/settingsSections'

/** navigationItems entry that renders as a separator line instead of a link. */
const NAV_ITEM_TYPE_DIVIDER = 'divider'
import AppLogo from '@/components/Logo'
import EmailVerificationBanner from '@/components/EmailVerificationBanner'
import PlanSelectionBanner from '@/components/private/layout/PlanSelectionBanner'
import TimezoneNoticeBanner from '@/components/TimezoneNoticeBanner'
import PortalAccountMenu from '@/components/private/layout/PortalAccountMenu'
import UserAccountMenu from '@/components/private/layout/UserAccountMenu'

// Import mypages CSS
import '@/assets/css/mypages.css'

export default {
  components: { EmailVerificationBanner, PlanSelectionBanner, TimezoneNoticeBanner, AppLogo, PortalAccountMenu, UserAccountMenu },
  name: 'App',
  data () {
    return {
      NAV_ITEM_TYPE_DIVIDER,
      ParticipantSegment,
      drawer: null,
    }
  },
  computed: {
    isLoggedIn: function() {
      return this.$store.getters['user/isLoggedIn']
    },
    username: function() {
      return this.$store.getters['user/name']
    },
    user: function() {
      return this.$store.getters['user/user']
    },
    /** "Rēķini" only appears once there is a receipt to show (server-decided, from GET /me). */
    hasPurchases: function() {
      return this.$store.getters['user/user']?.has_purchases === true
    },
    isAccountTypeParticipant: function() {
      return this.$store.getters['dashboard/isAccountTypeParticipant']
    },
    isAccountTypeOrganization: function() {
      return this.$store.getters['dashboard/isAccountTypeOrganization']
    },
    activeAccountIdentifier: function() {
      return this.$store.getters['dashboard/activeAccountIdentifier']
    },
    currentAccount: function() {
      return this.$store.getters['dashboard/currentAccount']
    },
    activeOrganization: function() {
      return this.$store.getters['dashboard/activeOrganization']
    },
    participantOrganizations: function() {
      return this.$store.getters['dashboard/participantOrganizations'] || []
    },
    /**
     * Whether this user may configure the organization. Mirrors the OWNER/ADMIN rule the API
     * already enforces, so a trainer is not shown a settings area that would reject them.
     */
    canConfigureOrganization: function() {
      const organizationId = this.$store.getters['dashboard/activeOrganizationId']
      return this.$store.getters['user/canConfigureOrganization'](organizationId)
    },
    isMembershipModuleEnabled: function() {
      const organization = this.$store.getters['dashboard/activeOrganization']
      return isMembershipModuleEnabled(organization)
    },
    /** Whether the current page is the settings index or one of its sections (opens the submenu). */
    isSettingsRouteActive: function() {
      return isSettingsRoute(this.$route.name)
    },
    /**
     * The gateway's own payment list, or null when the studio does not take online payments.
     * Built by the backend, which knows whether the organization runs in test or live mode.
     */
    paymentDashboardUrl: function() {
      return this.activeOrganization?.payment_dashboard_url || null
    },
    /** "Maksājumi (Stripe)" when the gateway is known, plain "Maksājumi" otherwise. */
    paymentsNavTitle: function() {
      const label = this.$t('layout.mypages.navigation.payments')
      const gateway = PAYMENT_GATEWAY_DISPLAY_NAME[this.activeOrganization?.payment_settings?.payment_gateway]
      return gateway ? `${label} (${gateway})` : label
    },
    /**
     * Drawer items: daily work | catalogues | settings, separated by dividers.
     *
     * A plain TRAINER sees the calendar and the participants and nothing else. Everything below
     * that line is owner/admin: the catalogues show prices and other commercial data, sold
     * memberships are money, and the settings decide how the studio charges (decision 2026-09-15,
     * reconfirmed when the class list moved out of the settings group).
     * Settings sub-items come from the settings domain registry.
     */
    navigationItems: function() {
      const link = (labelKey, icon, path) => ({
        title: this.$t(`layout.mypages.navigation.${labelKey}`),
        icon,
        route: this.getLocalisedMyPagesUrl(path)
      })
      const divider = { type: NAV_ITEM_TYPE_DIVIDER }

      const items = [
        link('calendar', 'mdi-calendar', MyPagesPath.EVENTS),
        link('participants', 'mdi-account-group', MyPagesPath.PARTICIPANTS)
      ]
      if (!this.canConfigureOrganization) return items

      if (this.isMembershipModuleEnabled) {
        items.push(link('memberships', 'mdi-format-list-bulleted-type', MyPagesPath.MEMBERSHIPS))
      }
      if (this.paymentDashboardUrl) {
        // Leaves the app: the payment list lives in the studio's own gateway account, which is
        // where the money is. Named after the gateway so it is obvious where the click lands.
        items.push({
          title: this.paymentsNavTitle,
          icon: 'mdi-credit-card-outline',
          href: this.paymentDashboardUrl,
          external: true
        })
      }
      items.push(divider)
      items.push(link('class-types', 'mdi-dumbbell', MyPagesPath.CLASSES))
      if (this.isMembershipModuleEnabled) {
        items.push(link('membership-templates', 'mdi-card-account-details', MyPagesPath.MEMBERSHIP_TEMPLATES))
      }
      items.push(divider)
      items.push({
        ...link('configuration-settings', 'mdi-cog', MyPagesPath.SETTINGS),
        children: SETTINGS_SECTIONS.map(section => ({
          routeName: section.routeName,
          icon: section.icon,
          title: this.$t(getSettingsSectionTitleKey(section.id))
        }))
      })
      return items
    },
  },
  created: function(){
    if (!this.isLoggedIn)
      throw new Error('User is not logged in')

    this.$store.dispatch('user/refreshUserData')
  },
  methods:{
    ...urlUtil,
    logout: function(){
      this.$store.dispatch('user/logout')
    },
    getOrganizationPageUrl() {
      return this.getOrganizationPublicUrl(this.activeOrganization)
    },
    getOrganizationPublicUrl(org) {
      if (!org || !org.identifier) return '#'
      return urlUtil.getFullUrl(urlUtil.getLocalisedOrganizationUrl(org))
    },
    copyOrganizationPage() {
      if (this.activeOrganization) {
        const url = this.getOrganizationPageUrl()
        navigator.clipboard.writeText(url).then(() => {
          this.$toast.success('URL copied to clipboard')
        }).catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea')
          textArea.value = url
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
          this.$toast.success('URL copied to clipboard')
        })
      }
    },
    editOrganizationProfile() {
      this.$router.push(this.getLocalisedMyPagesUrl(MyPagesPath.MY_SITE))
    }
  }
};
</script>
<style>
.v-application .home-icon a {
  color: #eee;
  text-decoration: none;
  font-size: 22px;
}
.v-application.as-page .as-header a {
  color: #eee;
  text-decoration: none;
}
.v-application .lang-menu {
  cursor: pointer;
}
.v-application.as-page .as-header .as-organization-link {
  color: var(--color-pallet-1) !important;
  font-weight: 600;
  font-size: 24px
}
.v-application.as-page .as-header .as-organization-link .v-avatar {
  display: inline;
}

/* Auth button styling */
.signup-btn {
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 2px 8px rgba(46, 139, 87, 0.3) !important;
  transition: all 0.3s ease !important;
}

.signup-btn:hover {
  box-shadow: 0 4px 12px rgba(46, 139, 87, 0.4) !important;
  transform: translateY(-1px) !important;
}

/* Modern Sidebar Styles */
.modern-sidebar {
  background: linear-gradient(90deg, var(--color-pallet-5) 20%, var(--color-pallet-6) 100%);
  border: none;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 280px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* This app bar is fixed at 64px, so the sticky timezone notice must park below it, not behind it.
   Public studio pages need no override - their app bar is `absolute` and scrolls away. */
.mypage-view .as-timezone-notice {
  --as-notice-sticky-top: 64px;
}

/* Let Vuetify handle positioning automatically */
/* Just ensure proper z-index for the app bar */
.v-application .v-app-bar {
  z-index: 100 !important;
}

/* Fix z-index for all Vuetify components to work properly with sidebar */
.v-dialog {
  z-index: 1000 !important;
}

.v-menu__content {
  z-index: 1000 !important;
}

.v-bottom-sheet {
  z-index: 1000 !important;
}

.v-overlay {
  z-index: 99 !important;
}

.v-navigation-drawer {
  z-index: 100 !important;
}

.organization-profile-section {
  background: rgba(0, 0, 0, 0.1);
  margin: 0;
  padding: 20px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
  text-align: center;
}

.organization-profile-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.organization-logo {
  flex-shrink: 0;
  margin-bottom: 0;
}

.organization-logo .v-avatar {
  background: var(--color-pallet-1) !important;
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(46, 139, 87, 0.3);
}

.organization-logo .v-icon {
  font-size: 30px !important;
  color: white !important;
  animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.organization-logo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

/* Loading state for logo */
.organization-logo .v-avatar {
  position: relative;
}

.organization-logo .v-avatar::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.organization-info {
  color: white;
  width: 100%;
  flex: 1;
  text-align: center;
}

.name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}
.name-row-center {
  flex: 1;
  min-width: 0;
  text-align: center;
}

.name-row-clickable {
  cursor: pointer;
  border-radius: 6px;
  padding: 2px 4px;
  margin: -2px -4px;
}
.name-row-clickable:hover {
  background: rgba(255, 255, 255, 0.1);
}

.name-row-chevron {
  flex-shrink: 0;
  color: white;
}

.organization-name {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: white;
  line-height: 1.3;
  text-align: center;
}

.organization-user-name {
  font-size: 14px;
  margin: 0 0 4px 0;
  opacity: 0.9;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.3;
  font-weight: 400;
  text-align: center;
}

.trainer-name {
  font-size: 14px;
  margin: 0;
  opacity: 0.8;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.3;
  text-align: center;
}

/* Next line: participant org icons with logos */
.participant-orgs-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  padding-top: 8px;
}

.participant-org-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.participant-org-link {
  display: inline-flex;
  text-decoration: none;
  border-radius: 50%;
}

.participant-org-link:hover .participant-org-avatar {
  opacity: 0.9;
  transform: scale(1.05);
}

.participant-org-avatar {
  transition: opacity 0.2s, transform 0.2s;
}

.organization-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 8px;
}

.sidebar-divider {
  background: rgba(255, 255, 255, 0.1);
  margin: 16px;
}

.navigation-container {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Profile Section Divider */
.profile-divider {
  border-color: var(--color-pallet-1) !important;
  margin: 0 16px;
  border-width: 2px !important;
}

.sidebar-menu {
  background: transparent !important;
  padding: 8px !important;
  height: 100% !important;
}

.sidebar-menu-item {
  margin: 4px 0 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease !important;
  cursor: pointer !important;
  user-select: none !important;
  min-height: 44px !important;
  position: relative;
}

.sidebar-menu-item:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  transform: translateX(4px) !important;
}

.sidebar-menu-item-active {
  background: rgba(44, 164, 92, 0.1) !important;
  border-left: 4px solid var(--color-pallet-1) !important;
  padding-left: 12px !important;
  box-shadow: none !important;
}

.sidebar-menu-item-active .v-list-item__content .v-list-item__title {
  color: white !important;
  font-weight: 600 !important;
}

.sidebar-menu-item-active .v-icon {
  color: white !important;
}

.sidebar-icon {
  margin-right: 12px !important;
  min-width: 24px !important;
}

.sidebar-divider {
  border-color: rgba(255, 255, 255, 0.12) !important;
  margin: 8px 12px !important;
}

.sidebar-submenu-item {
  margin: 2px 0 !important;
  padding-left: 40px !important;
  min-height: 40px !important;
  border-radius: 8px !important;
}

.sidebar-submenu-item:hover {
  background: rgba(255, 255, 255, 0.08) !important;
}

.sidebar-submenu-item .sidebar-text {
  font-size: 13px !important;
  font-weight: 400 !important;
}

.sidebar-submenu-item-active {
  background: rgba(44, 164, 92, 0.18) !important;
}

.sidebar-submenu-item-active .sidebar-text {
  font-weight: 600 !important;
}

.sidebar-text {
  font-weight: 500 !important;
  color: white !important;
}

.sidebar-section-title {
  font-size: 12px !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  opacity: 0.7 !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

.sidebar-menu-section-title {
  min-height: 32px !important;
  padding: 8px 16px !important;
}

.sidebar-logout {
  margin-top: 16px !important;
}

.sidebar-logout:hover {
  background: rgba(220, 53, 69, 0.2) !important;
}

.sidebar-logout .v-icon {
  color: var(--color-red-1) !important;
}

.sidebar-logout:hover .v-icon {
  color: var(--color-red-2) !important;
}

/* Bottom navigation section styling */
.v-navigation-drawer__append {
  flex-shrink: 0;
}

.v-navigation-drawer__append .sidebar-menu {
  padding: 8px !important;
  margin: 0 !important;
}

.v-navigation-drawer__append .sidebar-menu-item {
  margin: 4px 0 !important;
}

.quick-actions {
  padding: 20px 16px;
  flex-shrink: 0;
  background: rgba(0, 0, 0, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.quick-actions-title {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  text-align: center;
}

.quick-action-btn {
  border-radius: 8px;
  font-weight: 500;
  text-transform: none;
  height: 40px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  margin-bottom: 8px;
}

.quick-action-btn:last-child {
  margin-bottom: 0;
}

.quick-action-btn:active {
  transform: scale(0.98);
  transition: transform 0.1s ease;
}

/* Mobile navigation drawer fixes */
@media (max-width: 960px) {
  /* Mobile sidebar positioning - override desktop styles */
  .v-navigation-drawer {
    top: 0 !important;
    height: 100% !important;
    z-index: 100 !important;
  }
  
  .v-navigation-drawer--temporary {
    z-index: 100 !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    height: 100% !important;
    width: 70% !important;
    max-width: 280px !important;
    transform: translateX(0) !important;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }
  
  .v-navigation-drawer--temporary.v-navigation-drawer--closing {
    transform: translateX(-100%) !important;
  }
  
  .v-navigation-drawer--temporary.v-navigation-drawer--open {
    transform: translateX(0) !important;
  }
  
  /* Ensure the drawer content is visible */
  .v-navigation-drawer__content {
    background: linear-gradient(180deg, var(--color-pallet-5) 0%, #2c3e50 100%) !important;
    width: 100% !important;
    height: 100% !important;
    z-index: 100 !important;
  }
  
  /* Fix overlay issues and ensure proper overlay behavior */
  .v-navigation-drawer--temporary .v-overlay {
    z-index: 99 !important;
    background: rgba(0, 0, 0, 0.5) !important;
  }
  
  /* Ensure overlay covers the full screen */
  .v-overlay--active {
    z-index: 99 !important;
  }
  
  .organization-profile-section {
    padding: 16px 12px;
    text-align: center;
  }
  
  .organization-profile-content {
    gap: 10px;
    margin-bottom: 14px;
  }
  
  .organization-logo {
    margin-bottom: 0;
  }
  
  .organization-info {
    text-align: left;
  }
  
  .organization-name {
    font-size: 16px;
    margin: 0 0 3px 0;
    text-align: left;
  }
  
  .trainer-name {
    font-size: 12px;
    text-align: center;
  }
  
  .organization-actions {
    gap: 10px;
    margin-top: 6px;
  }
  .navigation-container {
    flex: 1;
  }
  
  .sidebar-menu {
    padding: 6px !important;
  }
  
  .sidebar-menu-item {
    min-height: 40px !important;
    margin: 2px 0 !important;
  }
}

/* Extra small mobile devices */
@media (max-width: 480px) {
  .modern-sidebar {
    width: 70% !important;
    max-width: 70vw !important;
  }
  
  .organization-profile-section {
    padding: 14px 10px;
    text-align: center;
  }
  
  .organization-profile-content {
    gap: 12px;
    margin-bottom: 12px;
  }
  
  .organization-logo {
    margin-bottom: 5px;
  }
  
  .organization-info {
    text-align: center;
  }
  
  .organization-name {
    font-size: 16px;
    margin: 0 0 4px 0;
    text-align: center;
  }
  
  .trainer-name {
    font-size: 13px;
    text-align: center;
  }
  
  .organization-actions {
    gap: 8px;
    margin-top: 5px;
  }
  .sidebar-menu {
    padding: 4px !important;
  }
  
  .sidebar-menu-item {
    min-height: 36px !important;
    margin: 1px 0 !important;
  }
}

/* Footer background to match Sidebar */
.v-footer .v-card {
  background: linear-gradient(180deg, var(--color-pallet-5) 0%, #2c3e50 100%) !important;
  color: white !important;
}

/* New Footer Styles for MyPages Layout - Scoped to avoid conflicts */
.as-mypages-layout .as-footer {
  background: #f5f5f5 !important;
  border-top: 1px solid #e0e0e0;
  padding: 16px !important;
  color: #666 !important;
}

.as-mypages-layout .footer-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.as-mypages-layout .footer-text {
  margin: 0 0 8px 0;
  color: #666;
  font-size: 14px;
  line-height: 1.4;
}

.as-mypages-layout .footer-text:last-child {
  margin-bottom: 0;
}

.as-mypages-layout .footer-link {
  color: var(--color-pallet-1);
  text-decoration: none;
  transition: color 0.2s ease;
  font-weight: 500;
}

.as-mypages-layout .footer-link:hover {
  color: #2e7d32;
  text-decoration: underline;
}

/* Mobile responsive footer */
@media (max-width: 768px) {
  .as-mypages-layout .as-footer {
    padding: 12px !important;
  }
  
  .as-mypages-layout .footer-content {
    max-width: 100%;
  }
  
  .as-mypages-layout .footer-text {
    font-size: 13px;
  }
}

.my-pages-layout {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.main-content {
  flex: 1;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.content-container {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

/* Mobile-specific improvements */
@media (max-width: 960px) {
  .content-container {
    padding: 16px;
  }
}

/* Extra small mobile devices */
@media (max-width: 480px) {
  .content-container {
    padding: 12px;
  }
}
</style>